import React, { useState } from 'react';
import { ClientApiProps, IAssessorApplicationEx } from '../../common';
import { Address, AssessorApplication, Acma_amateurradioassessor_statuscode, IAssessorApplication, getApiClient, msalGetToken } from '../../api';
import { AlertProvider, FileDownloadFormats, Form, downloadBlob } from 'acma-spa';
import { useNavigate, useParams } from 'react-router-dom';
import { AssessorApplicationPage } from './Page';

export interface AssessorApplicationFormParams {
  [keys: string]: string | undefined;
  id?: string;
}

export interface AssessorApplicationFormProps extends ClientApiProps {}

export const AssessorApplicationForm: React.FC<AssessorApplicationFormProps> = (props) => {
  const params = useParams<AssessorApplicationFormParams>();
  const routerNavigate = useNavigate();

  const [files, setFiles] = useState<Record<string, File | null>>({});
  const [readOnly, setReadOnly] = useState(false);

  const refresh = (src: IAssessorApplicationEx) => {
    const newFiles: Record<string, File | null> = {};
    if (src.fileNames) {
      for (const f of src.fileNames) {
        newFiles[f] = null;
      }
    }
    setFiles(newFiles);
    setReadOnly(src.status != Acma_amateurradioassessor_statuscode.Draft);
  };

  const load: { (): Promise<[IAssessorApplicationEx, number]> } = async () => {
    const [client] = getApiClient(props.baseUrl, props.b2cDomainName, props.msalClient);
    const res = params.id ? await client.assessorApplicationGetById(params.id) : await client.assessorApplicationGetCurrent();

    if (res != null) {
      refresh(res);
      return [res, 0];
    } else {
      setFiles({});

      const operator = await client.operatorGet(undefined);

      let init: IAssessorApplicationEx;
      if (operator.contact) {
        init = { ...operator.contact };
      } else {
        let idTokenClaims: Record<string, any> | undefined = {};
        const accountInfo = props.msalClient.getAllAccounts();
        if (accountInfo.length > 0) {
          idTokenClaims = accountInfo[0].idTokenClaims;
        }

        init = {
          firstName: idTokenClaims?.given_name,
          lastName: idTokenClaims?.family_name,
          emailAddress: idTokenClaims?.email,
          advertisedEmail: idTokenClaims?.email,
        };
      }
      return [init, 0];
    }
  };

  const getNextPage = (page: number, m: IAssessorApplicationEx) => {
    let nextPage = page;
    if (page < 0) {
      nextPage = page + 1;
    }
    return nextPage;
  };

  const getPreviousPage = (page: number) => {
    if (page > 0) return page - 1;
    else return page;
  };

  const save = async (m: IAssessorApplicationEx, submit?: boolean): Promise<IAssessorApplicationEx> => {
    const application = new AssessorApplication({
      ...m,
      address1: new Address(m.address1),
      advertisedAddress: new Address(m.advertisedAddress),
      fileNames: Object.keys(files),
    });

    const formData = new FormData();
    formData.append('data', JSON.stringify(application.toJSON()));

    const fileNames: string[] = [];
    for (const name in files) {
      const f = files[name];
      if (f == null) continue;

      formData.append('files', f);
      fileNames.push(name);
    }

    formData.append('fileNames', JSON.stringify(fileNames));

    const authToken = await msalGetToken(props.b2cDomainName, props.msalClient);
    const res = await fetch(`${props.baseUrl}/api/AssessorApplications`, {
      method: submit ? 'POST' : 'PUT',
      body: formData,
      headers: {
        Authorization: `Bearer ${authToken}`,
      },
    });

    if (res.status != 200) throw new Error(res.statusText);

    const data: IAssessorApplicationEx = await res.json();
    refresh(data);
    return data;
  };

  const onFileAdded = (arr: File[]) => {
    const newFiles = { ...files };

    for (const f of arr) {
      const index = f.name.lastIndexOf('.');
      let filename = f.name;
      let extension = '';

      if (index >= 0) {
        filename = f.name.substring(0, index);
        extension = f.name.substring(index);
      }

      for (let j = 0; ; j++) {
        let key = f.name;
        if (j > 0) {
          key = `${filename} (${j})${extension}`;
        }
        if (!(key in newFiles)) {
          newFiles[key] = f;
          break;
        }
      }
    }
    setFiles(newFiles);
    return Object.keys(newFiles);
  };

  const onFileRemoved = (name: string) => {
    const newFiles = { ...files };
    if (name in newFiles) delete newFiles[name];
    setFiles(newFiles);
    return Object.keys(newFiles);
  };

  return (
    <>
      <h1>Application to be an ACMA amateur radio accredited assessor</h1>

      {readOnly && (
        <AlertProvider color="info" messages={['The form has already been submitted. Please contact info@acma.gov.au to make any changes to the form.']} />
      )}

      <Form<IAssessorApplicationEx>
        onLoad={load}
        getNextPage={getNextPage}
        getPreviousPage={getPreviousPage}
        onPrint={(m, f) =>
          downloadBlob(
            `${props.baseUrl}/api/AssessorApplications/print/${f}`,
            props.msalClient,
            [`https://${props.b2cDomainName}.onmicrosoft.com/AcmaApis/Call`],
            `${m.referenceNumber ?? 'registration'}.${f == FileDownloadFormats.Pdf ? 'pdf' : 'docx'}`,
            f == FileDownloadFormats.Pdf ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
            m,
          )
        }
        readOnly={readOnly}
        onSave={save}
        onSubmit={(m) => save(m, true)}
        onExit={(m, submit) => {
          console.log('exiting');
          routerNavigate('/forms');
          return undefined;
        }}>
        {(ctx) => {
          switch (ctx?.page) {
            case 0:
              return <AssessorApplicationPage {...props} ctx={ctx} onFileAdded={onFileAdded} onFileRemoved={onFileRemoved} />;
            default:
              return <></>;
          }
        }}
      </Form>
    </>
  );
};
